import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { SkeletonText } from '@carbon/react';

import { useNotifications } from 'core/notifications';
import AdditionalSpacesForm from 'features/inputs/AdditionalSpacesForm';
import ProjectFormNavigation from 'features/inputs/components/ProjectFormNavigation';
import QuickView from 'features/inputs/components/QuickView';
import { MeasurementSystemProvider } from 'features/inputs/contexts/measurementSystemContext';
import EmployeePopulationForm from 'features/inputs/EmployeePopulationForm';
import ProjectDetailsForm from 'features/inputs/ProjectDetailsForm';
import { MEASUREMENT_SYSTEM } from 'models/measurementSystem';
import useMutateUpdateProjectMeasurementSystem from 'shared/api/projects/useMutateUpdateProjectMeasurementSystem';
import { createUseProjectOutputsQueryKey } from 'shared/api/projects/useProjectOutputsQuery';
import { createUseProjectQueryKey } from 'shared/api/projects/useProjectQuery';
import PageDescription from 'shared/components/PageDescription';
import ProjectBreadcrumbs, { PROJECT_BREADCRUMBS_PAGE } from 'shared/components/ProjectBreadcrumbs';
import ProjectTier from 'shared/components/ProjectTier';
import useLatest from 'utils/hooks/useLatest';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';
import styles from 'theme/features/inputs/inputs-form.module.scss';

const InputsForm = ({
  project = null,
  isLoadingProject = false,
  overview,
  isLoadingOverview = false,
  isEditView = false
}) => {
  const queryClient = useQueryClient();

  const { showErrorNotification } = useNotifications();

  const projectId = project?.details?.id ?? null;
  const projectIdRef = useLatest(projectId);
  const [values, setValues] = useState(project ?? {});
  const [measurementSystem, setMeasurementSystem] = useState(
    isEditView ? project?.details?.measurementSystem ?? null : MEASUREMENT_SYSTEM.imperial
  );

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const updateProjectMeasurementSystemMutation = useMutateUpdateProjectMeasurementSystem({
    onSuccess: (_, { measurementSystem }) => {
      const updateSystem = (prevProject) => ({
        ...prevProject,
        details: {
          ...prevProject.details,
          measurementSystem
        }
      });

      setValues(updateSystem);
      queryClient.setQueryData(createUseProjectQueryKey(projectIdRef.current), updateSystem);
    },
    onError: () => showErrorNotification({ description: 'Failed to update measurement system' })
  });

  useUpdateEffect(() => {
    if (!isLoadingProject && project) {
      setValues(project);
      setMeasurementSystem(project.details.measurementSystem);
    }
  }, [project, isLoadingProject]);

  const handleMeasurementSystemChange = (newMeasurementSystem) => {
    setMeasurementSystem(newMeasurementSystem);

    if (isEditView) {
      updateProjectMeasurementSystemMutation.mutate({
        id: projectIdRef.current,
        measurementSystem: newMeasurementSystem
      });
    }
  };

  const handleSaveChanges = (newProject) => {
    queryClient.setQueryData(createUseProjectQueryKey(newProject.details.id), newProject);
    queryClient.refetchQueries(createUseProjectOutputsQueryKey(newProject.details.id));

    setValues(newProject);
  };

  return (
    <MeasurementSystemProvider value={measurementSystem}>
      <div className={styles.wrapper}>
        <QuickView
          isLoading={isLoadingOverview}
          onMeasurementSystemChange={handleMeasurementSystemChange}
          overview={overview}
        />
        <div className={styles.content}>
          {isEditView && <ProjectBreadcrumbs projectId={projectId} page={PROJECT_BREADCRUMBS_PAGE.inputs} />}
          {isLoadingProject ? (
            <SkeletonText className={styles['header-skeleton']} />
          ) : (
            <h1>
              <strong>{isEditView ? project.details.name : 'Create a new project!'}</strong>
              {values?.details?.tier != null && (
                <ProjectTier
                  tier={values.details.tier}
                  isValid={values.details.tierIsValid}
                  withTooltip
                  size="lg"
                  inline
                />
              )}
            </h1>
          )}
          <PageDescription>
            Explore options for your real estate needs, quickly and easily, <br /> with the information you have
          </PageDescription>
          <ProjectFormNavigation
            isEditView={isEditView}
            isLoadingProject={isLoadingProject}
            selectedIndex={selectedTabIndex}
            onIndexChange={setSelectedTabIndex}
            headers={[
              'Project Details (Required)',
              'Group Population (Required)',
              'Specialty & Site-specific Spaces (Optional)'
            ]}
          >
            <ProjectDetailsForm
              values={values.details}
              onSaveChanges={handleSaveChanges}
              isEditView={isEditView}
              isLoading={isLoadingProject || (isEditView && !values.details)}
            />
            <EmployeePopulationForm
              values={values.employeePopulation ?? []}
              onSaveChanges={handleSaveChanges}
              projectId={projectId}
            />
            <AdditionalSpacesForm
              specialtySpaces={values.specialtySpaces}
              siteSpecificSpaces={values.siteSpecificSpaces ?? []}
              onSaveChanges={handleSaveChanges}
              projectId={projectId}
            />
          </ProjectFormNavigation>
        </div>
      </div>
    </MeasurementSystemProvider>
  );
};

export default InputsForm;
